import {
  ApproveSuccessAction,
  useConfirmApproveModal,
} from '@/features/approving_invoice_reports/components/ConfirmApproveModal/context/ConfirmApproveModalContext';
import {
  RejectSuccessAction,
  useConfirmRejectModal,
} from '@/features/approving_invoice_reports/components/ConfirmRejectModal/context/ConfirmRejectModalContext';
import { saveListSnapshot } from '@/features/approving_invoice_reports/utils/listSnapshotStorage';
import { useKeepApprovalSearchConditionsFlag } from '@/hooks/feature_flag/useKeepApprovalSearchConditionsFlag';
import { useTranslation } from '@/i18n';
import {
  ButtonGroup,
  ButtonV2,
  LinkButton,
} from '@moneyforward/ap-frontend-components';
import classNames from 'classnames/bind';
import { FC, memo } from 'react';
import styles from './styles.module.scss';

const cx = classNames.bind(styles);

export type HeaderButtonsProps = {
  is_report_approval: boolean;
  is_editable: boolean;
  is_approvable: boolean;
  reportId: string;
  /** NA-5815 / NA-5831: row index of this drawer in the list, saved with the snapshot. */
  selectedIndex?: number;
};

export const HeaderButtons: FC<HeaderButtonsProps> = memo(
  ({
    is_report_approval = false,
    is_editable = false,
    is_approvable = false,
    reportId,
    selectedIndex,
  }) => {
    const { t } = useTranslation();
    const { onOpenConfirmRejectModal } = useConfirmRejectModal();
    const { onOpenConfirmApproveModal } = useConfirmApproveModal();
    const keepSearchConditionsEnabled = useKeepApprovalSearchConditionsFlag();

    const detailHref = `/approving_invoice_reports/${reportId}`;

    // NA-5815 / NA-5831: remember the list URL (incl. this drawer's `id`), the
    // row and the request before leaving, so the list can restore it when the
    // action succeeds.
    const saveSnapshot = () =>
      saveListSnapshot({
        search: window.location.search,
        selectedIndex,
        reportId,
      });

    const goToReportDetail = () => {
      window.location.href = detailHref;
    };

    const goToReportEdit = () => {
      if (keepSearchConditionsEnabled) saveSnapshot();
      window.location.href = `${detailHref}/edit`;
    };

    // Flag on: a real link so the detail page can also be opened in a new tab.
    // LinkButton has no onClick, so the click is observed on the wrapper.
    const detailLink = keepSearchConditionsEnabled ? (
      <span onClick={saveSnapshot}>
        <LinkButton size='sm' isTertiary href={detailHref}>
          {t('detail')}
        </LinkButton>
      </span>
    ) : (
      <ButtonV2 size='sm' isTertiary onClick={goToReportDetail}>
        {t('detail')}
      </ButtonV2>
    );

    const renderButtons = () => {
      if (is_report_approval) {
        return (
          <>
            {detailLink}
            <ButtonV2
              isDisabled={!is_editable}
              size='sm'
              isSecondary
              onClick={goToReportEdit}
            >
              {t('edit')}
            </ButtonV2>
            <div className={cx('buttons-separator')} />

            <div className={cx('reject-approve-buttons')}>
              <ButtonV2
                size='sm'
                color='danger'
                isSecondary
                onClick={() =>
                  onOpenConfirmRejectModal(
                    reportId,
                    RejectSuccessAction.GOTO_NEXT_TARGET
                  )
                }
              >
                {t('approving_invoice_report.drawer.reject_button')}
              </ButtonV2>

              <ButtonV2
                isDisabled={!is_approvable}
                size='sm'
                color='primary'
                onClick={() =>
                  onOpenConfirmApproveModal({
                    reportTarget: reportId,
                    approveSuccessAction: ApproveSuccessAction.GOTO_NEXT_TARGET,
                  })
                }
              >
                {t('approving_invoice_report.drawer.approve_button')}
              </ButtonV2>
            </div>
          </>
        );
      }

      return detailLink;
    };

    return <ButtonGroup>{renderButtons()}</ButtonGroup>;
  }
);
HeaderButtons.displayName = 'HeaderButtons';
